/**
 * Entity spawning, AI and the encounter director.
 *
 * The system owns the bodies: it refreshes what every entity knows about the
 * player, runs a bounded number of behaviour ticks per frame, integrates
 * movement with collision, and reports a single honest `threat` number that the
 * audio layer turns into music. Behaviours own only policy — see `brain.ts`.
 *
 * Almost all of the time there is nothing in `entities` at all. That is the
 * intended state of the game; see `director.ts` for why.
 */

import { TUNING } from '../core/config';
import { clamp01, damp, smoothstep, turnToward } from '../core/math';
import type { EntitySystem } from '../core/systems';
import type {
  EntityKind,
  EntityMode,
  EntityState,
  EventBus,
  GameContext,
  GeneratedLevel,
  Grid,
  RNG,
  Vec2,
} from '../core/types';
import { makeRNG } from '../core/rng';
import { getBehavior } from './behaviors';
import { noteProgress, setMode, type Agent, type EntityWorld } from './brain';
import { getEntityDef, pursues } from './defs';
import { makeDirector, type Director, type DirectorHost } from './director';
import {
  blocked,
  clearPath,
  makeNavigator,
  makePath,
  makePropField,
  nearestWalkable,
  slideMove,
  type Navigator,
  type PropField,
} from './nav';
import { noiseAt, playerCanSee } from './perception';

/**
 * Behaviour ticks per frame. A level normally holds nought or one entity, so
 * this never binds; it exists so twenty would cost the same as six.
 */
const MAX_THINKS_PER_FRAME = 6;
/** Seconds between an agent's behaviour ticks. Movement still runs every frame. */
const THINK_INTERVAL = 1 / 20;
/** Velocity smoothing rate, 1/seconds. High enough that a hound reads as urgent. */
const ACCEL_RATE = 9;
/**
 * The same, for a body that freezes when watched. High enough to be at speed
 * within a frame or two of being released, because the whole effect is that it
 * was not moving and now it is. See `integrate`.
 */
const FREEZE_ACCEL_RATE = 45;
/** Metres from a waypoint at which an agent starts easing off, so it cannot orbit. */
const ARRIVE_EASE = 0.8;
/** Metres the player can see an entity at all. Beyond the fog it does not count as seen. */
const MAX_SIGHT_RANGE = 44;

/** Snag bookkeeping. Documented in `core/config.ts`. */
const SNAG_DECAY = TUNING.entities.snagDecay;
const SNAG_LIMIT = TUNING.entities.snagLimit;
const PROP_GRACE = TUNING.entities.propGrace;

/** How much each mode contributes to the danger the player is actually in. */
const MODE_THREAT: Record<EntityMode, number> = {
  dormant: 0.04,
  wander: 0.1,
  patrol: 0.14,
  investigate: 0.45,
  stalk: 0.6,
  chase: 1,
  attack: 1,
  flee: 0.12,
  dead: 0,
};

/** Per-kind ceiling on threat. A clump beside you is not a hound behind you. */
const KIND_THREAT: Record<EntityKind, number> = {
  hound: 1,
  bacteria: 1,
  partygoer: 0.95,
  centipede: 0.9,
  // Below the things that run at you, on purpose. Its dread is the silence and
  // the fact that it is nearer than it was; a music cue that swells while it
  // stands still would answer the question the player is supposed to be asking.
  faceling: 0.7,
  smiler: 0.55,
  deathmoth: 0.5,
  clump: 0.35,
  hollow: 0.3,
};

/** Modes that count as hunting, for the pursuit licence. See `Agent.longHunt`. */
const HUNTING_MODES: ReadonlySet<EntityMode> = new Set<EntityMode>([
  'investigate',
  'stalk',
  'chase',
  'attack',
]);

class Entities implements EntitySystem {
  /**
   * Never reassigned. `game.ts` captures this array once when it builds the
   * frame context, so replacing it would silently detach every other system.
   */
  private readonly list: EntityState[] = [];
  private readonly agents: Agent[] = [];
  private nextId = 1;

  private level: GeneratedLevel | null = null;
  private grid: Grid | null = null;
  /** The level's solid scenery. Null until a level with props is loaded. */
  private props: PropField | null = null;
  private rng: RNG = makeRNG(1);

  private readonly nav: Navigator = makeNavigator();
  private readonly director: Director;
  private readonly world: EntityWorld;

  /** Round-robin cursor so the think scheduler is fair with many entities. */
  private cursor = 0;

  /**
   * Kinds the player has already met this run. Per *run*, not per level: the
   * lesson a first encounter teaches does not need teaching again one floor
   * down. See `Agent.firstOfRun`.
   */
  private readonly met = new Set<EntityKind>();
  private readonly unsubscribe: Array<() => void> = [];

  threat = 0;

  constructor(private readonly events: EventBus) {
    // The only signal that separates "a new run" from "the next level". A death
    // and a restart must put the teaching back, or the second run of a session
    // is the one that kills a player who never saw the first hound.
    this.unsubscribe.push(
      this.events.on('game:start', () => {
        this.met.clear();
        // Same argument, one layer down: the slide surge says what it is once
        // per run, and a restart is a player who has not been told yet.
        this.director.newRun();
      }),
    );

    const host: DirectorHost = {
      agents: this.agents,
      // The agent rather than the state: the director has to be able to mark
      // what it just placed as a long hunter, and only the agent carries that.
      spawn: (kind, pos) => this.spawnAgent(kind, pos),
      despawn: (id) => this.despawn(id),
    };
    this.director = makeDirector(host);

    // Built once and mutated in place; behaviours run every frame.
    this.world = {
      ctx: null as unknown as GameContext,
      dt: 0,
      grid: null as unknown as Grid,
      props: null,
      level: null as unknown as GeneratedLevel,
      player: null as unknown as GameContext['player'],
      events: this.events,
      audio: null as unknown as GameContext['audio'],
      nav: this.nav,
      rng: this.rng,
      playerLight: 0,
      sanity: 100,
      // Guarded: `spawn` is public and may be called before the first frame.
      lightAt: (pos: Vec2) => (this.world.ctx ? this.world.ctx.render.lightLevelAt(pos) : 0),
      // Reported, never applied. `SurvivalStats.sanity` has exactly one writer.
      dread: (state, rate) => {
        if (rate > 0) this.events.emit('entity:dread', { id: state.id, kind: state.kind, rate });
      },
      alertKin: (from, kind, loudness, radius) => this.alertKin(from, kind, loudness, radius),
    };
  }

  /**
   * One entity telling the others of its kind where the player is. Written
   * exactly the way `hearNoise` writes a noise, so a called partygoer still has
   * to cross the floor and still has to find the player at the end of it.
   *
   * The one thing a call does that an ordinary noise does not is wake the
   * sleeper. A noise handed to an agent outside `activeRadius` is thrown away
   * unread — it never thinks, and falling asleep clears the pending noise — so
   * a call across the floor did nothing at all: measured, two partygoers 85 m
   * out stayed dormant for a hundred seconds while a third one screamed. That
   * makes "you can never catch a Partygoer alone" false in exactly the case it
   * is supposed to be true. So a call also hands a pursuit licence to anything
   * that can act on it, which raises that agent's own sleep radius, wakes it
   * next frame and lets it start walking.
   *
   * Gated on `pursues` for the same reason everything else is: a kind that
   * would never arrive must not be woken across a distance it cannot cross,
   * because a licensed thing is also a thing the director will not recycle.
   */
  private alertKin(from: EntityState, kind: EntityKind, loudness: number, radius: number): void {
    const r2 = radius * radius;
    const canCome = pursues(kind);
    for (const a of this.agents) {
      const s = a.state;
      if (s.id === from.id || s.kind !== kind) continue;
      const dx = s.pos.x - from.pos.x;
      const dz = s.pos.z - from.pos.z;
      if (dx * dx + dz * dz > r2) continue;
      if (a.heardPending && a.heardLoudness >= loudness) continue;
      // The caller's own idea of where the player is, not the player's actual
      // position: a call is hearsay, and hearsay is allowed to be wrong.
      const known = from.lastKnown;
      if (!known) continue;
      a.heardPos.x = known.x;
      a.heardPos.z = known.z;
      a.heardLoudness = loudness;
      a.heardPending = true;
      if (canCome) a.longHunt = Math.max(a.longHunt, TUNING.entities.pursuit.licence);
    }
  }


  get entities(): readonly EntityState[] {
    return this.list;
  }

  setLevel(level: GeneratedLevel, levelIndex = 0): void {
    this.clear();
    this.level = level;
    this.grid = level.grid;
    // Built from the same index the player collides against, so a bathtub that
    // stops them stops a hound in exactly the same place.
    const field = makePropField(level.grid, level.props, level.propIndex);
    this.props = field.count > 0 ? field : null;
    this.rng = makeRNG(level.seed ^ 0x5bd1e995);
    this.world.rng = this.rng;
    this.world.level = level;
    this.world.grid = level.grid;
    this.world.props = this.props;
    this.nav.setGrid(level.grid, this.props);
    this.director.setLevel(level, levelIndex);
    // `level.entitySpawns` is deliberately not used. A level that already has a
    // monster standing in it the moment you arrive is not this game; the
    // director places everything, after the grace period, out of sight.
  }

  update(ctx: GameContext): void {
    const grid = this.grid;
    if (!grid) return;

    const w = this.world;
    w.ctx = ctx;
    w.player = ctx.player;
    w.audio = ctx.audio;
    w.playerLight = ctx.render.lightLevelAt(ctx.player.pos);
    w.sanity = ctx.stats.sanity;

    this.nav.update();

    const dt = ctx.dt;
    let best = 0;

    for (let i = this.agents.length - 1; i >= 0; i--) {
      const a = this.agents[i]!;
      const s = a.state;

      s.age += dt;
      s.modeTime += dt;
      a.attackCooldown = Math.max(0, a.attackCooldown - dt);
      a.repathCooldown = Math.max(0, a.repathCooldown - dt);
      // Burnt in real time, not think time, so a hunt cannot be extended by the
      // scheduler skipping the agent on a busy frame.
      if (a.longHunt > 0) {
        a.longHunt = Math.max(0, a.longHunt - dt);
        if (a.longHunt === 0) a.surgeHunt = false;
      }

      this.refreshPerception(a, ctx, grid);

      // The pursuit licence, granted here rather than by a behaviour so that a
      // kind which cannot arrive can never be licensed by a behaviour author
      // forgetting the rule. It only ever tops the countdown up, so a surge
      // hunt's much longer licence is never cut short by an ordinary one.
      if (pursues(s.kind) && HUNTING_MODES.has(s.mode)) {
        a.longHunt = Math.max(a.longHunt, TUNING.entities.pursuit.licence);
      }

      // The weeping-angel rule, evaluated on the frame rather than on the think
      // tick because a player can turn ninety degrees between think ticks.
      // `EntityState.visible` is already most of the observation test — inside
      // the view cone, line of sight clear — and `refreshPerception` has just
      // refreshed it against this frame's camera. `freezeRange` narrows it to
      // the distance the behaviour also uses, so the two cannot disagree.
      //
      // Contact is the exception: once it is inside its own attack range, being
      // looked at stops helping. Which is also what keeps the rule from
      // deadlocking, since the behaviour stops trying to move at exactly the
      // distance the system stops holding it.
      a.frozen =
        a.freezeRange > 0 &&
        s.visible &&
        s.distToPlayer <= a.freezeRange &&
        s.distToPlayer > a.def.attackRange;

      if (s.distToPlayer > despawnRadiusFor(a)) {
        this.recycle(a);
        continue;
      }
      this.updateSleep(a, w);

      if (!a.asleep) {
        a.thinkAccum += dt;
        this.integrate(a, dt, grid);
      }

      const t = threatOf(a);
      if (t > best) best = t;
    }

    this.runThinks(w);
    this.separate(grid);
    this.reapRetired();

    // `w.dt` is a per-agent think delta; the director runs on the frame delta.
    w.dt = dt;
    this.director.update(w);

    // After the director, not before: the slide surge is dread with nothing
    // behind it yet, and it is the only warning a player gets that standing at
    // the way down is costing them. Folded in here because `threat` is what
    // `game.ts` hands to `audio.setTension`, so this is the seam where a
    // building that has noticed you sounds like one.
    best = Math.max(best, clamp01(this.director.surgeTension));

    // Rising dread should arrive faster than it leaves; a chase that ends does
    // not make the room feel safe again immediately.
    this.threat = damp(this.threat, best, best > this.threat ? 6 : 0.9, dt);
  }

  /* ---------------------------------------------------------------- *
   * Per-frame bookkeeping
   * ---------------------------------------------------------------- */

  /** Refresh `distToPlayer` and `visible`. Other systems read both every frame. */
  private refreshPerception(a: Agent, ctx: GameContext, grid: Grid): void {
    const s = a.state;
    s.distToPlayer = Math.hypot(s.pos.x - ctx.player.pos.x, s.pos.z - ctx.player.pos.z);

    const wasVisible = s.visible;
    s.visible = s.distToPlayer < MAX_SIGHT_RANGE && playerCanSee(grid, ctx.player, s.pos, MAX_SIGHT_RANGE);

    if (s.visible && !a.sighted) {
      a.sighted = true;
      this.events.emit('entity:sighted', { id: s.id, kind: s.kind, dist: s.distToPlayer });
    }
    if (s.visible && !wasVisible && a.sighted && s.kind === 'smiler') {
      ctx.audio.play('smiler-breath', { pos: s.pos, gain: 0.5 });
    }
  }

  /** Hysteresis so an entity hovering at the edge does not flap in and out. */
  private updateSleep(a: Agent, w: EntityWorld): void {
    const d = a.state.distToPlayer;
    const radius = activeRadiusFor(a);
    if (!a.asleep && d > radius) {
      a.asleep = true;
      this.nav.cancel(a.state.id);
      clearPath(a.path);
      setMode(a, w, 'dormant');
      this.resolveIfEngaged(a);
      a.state.vel.x = 0;
      a.state.vel.z = 0;
      // Falling asleep is the player having got away. It must not wake up still
      // certain of where they were, or walking back into the room is instant death.
      a.state.awareness = 0;
      a.state.lastKnown = null;
      a.searchTimer = 0;
      a.heardPending = false;
      // Whatever it was snagged on is far behind it now.
      a.snags = 0;
      a.propGrace = 0;
      // Falling asleep is also the end of a hunt. A hunter whose licence ran
      // out mid-approach has just forgotten the player; letting it keep the
      // raised radius after that would be a hunt with nothing behind it.
      a.longHunt = 0;
      a.surgeHunt = false;
      // A sleeping body is not being observed by anyone who matters, and a
      // stale freeze would stop it waking up properly.
      a.frozen = false;
    } else if (a.asleep && d < radius * 0.92) {
      a.asleep = false;
      a.thinkAccum = THINK_INTERVAL;
    }
  }

  /**
   * Run behaviour ticks for the agents that are due, capped per frame. With one
   * or two entities everything thinks at `THINK_INTERVAL`; with twenty the cap
   * spreads the work and each agent still gets its accumulated dt.
   */
  private runThinks(w: EntityWorld): void {
    const n = this.agents.length;
    if (n === 0) return;
    let budget = MAX_THINKS_PER_FRAME;
    for (let i = 0; i < n && budget > 0; i++) {
      const a = this.agents[(this.cursor + i) % n]!;
      if (a.asleep || a.retire || a.thinkAccum < THINK_INTERVAL) continue;
      w.dt = a.thinkAccum;
      a.thinkAccum = 0;
      budget--;
      a.behavior.think(a, w);
      this.checkResolution(a);
    }
    this.cursor = (this.cursor + 1) % n;
  }

  /** Steer, collide and turn. The only place an entity's position ever changes. */
  private integrate(a: Agent, dt: number, grid: Grid): void {
    const s = a.state;

    // Held. Velocity is zeroed rather than damped toward zero: the whole effect
    // is that it is not moving, and `ACCEL_RATE` would leave it coasting a
    // visible five centimetres into the moment the player's eyes land on it.
    // The turn is skipped too — a body that pivots to face you while claiming
    // to be a piece of furniture is worse than one that moves.
    if (a.frozen) {
      s.vel.x = 0;
      s.vel.z = 0;
      a.stuck = 0;
      return;
    }

    a.propGrace = Math.max(0, a.propGrace - dt);
    a.snags = Math.max(0, a.snags - SNAG_DECAY * dt);
    let tvx = 0;
    let tvz = 0;

    if (a.goalSpeed > 0.01) {
      const dx = a.goal.x - s.pos.x;
      const dz = a.goal.z - s.pos.z;
      const l = Math.hypot(dx, dz);
      if (l > 1e-4) {
        // Ease off on approach, or a body orbits its own waypoint forever.
        const ease = Math.min(1, l / ARRIVE_EASE);
        const v = a.goalSpeed * ease;
        tvx = (dx / l) * v;
        tvz = (dz / l) * v;
      }
    }

    // A thing that freezes does not accelerate: it is either still or moving,
    // and the moment the player's eyes leave it, it is moving. At the ordinary
    // rate a faceling released for a fifth of a second — about as long as
    // checking over your shoulder takes — spends most of that fifth winding up
    // and covers 0.4 m instead of 0.9 m. Measured over a minute of glancing
    // back every two seconds, the ramp cost it more than half the ground it
    // should have made. Nothing else in the game has a reason to be this
    // abrupt, so nothing else gets it.
    const accel = a.freezeRange > 0 ? FREEZE_ACCEL_RATE : ACCEL_RATE;
    s.vel.x = damp(s.vel.x, tvx, accel, dt);
    s.vel.z = damp(s.vel.z, tvz, accel, dt);

    const px = s.pos.x;
    const pz = s.pos.z;
    // During its grace period a body moves on the grid alone, so scenery it has
    // already proved it cannot get around does not hold it there.
    slideMove(grid, s.pos, s.vel.x * dt, s.vel.z * dt, a.def.radius, a.propGrace > 0 ? null : this.props);

    if (noteProgress(a, Math.hypot(s.pos.x - px, s.pos.z - pz), dt)) {
      // Wedged. Throw the route away and let the next think tick pick a new one.
      a.stuck = 0;
      a.repathCooldown = 0;
      clearPath(a.path);
      this.nav.cancel(s.id);
      // Twice in short order and routing is not what is wrong. Rather than
      // stand against a chair forever, walk through it for a moment.
      if (++a.snags >= SNAG_LIMIT) {
        a.snags = 0;
        a.propGrace = PROP_GRACE;
      }
      // Last resort: something has ended up inside geometry. Nothing else will
      // ever get it out, so put it back on the nearest floor cell. Props are
      // deliberately not consulted — `slideMove` ejects from those every frame,
      // and teleporting a body across the room because it brushed a bathtub
      // would be far more visible than the brush.
      if (blocked(grid, s.pos.x, s.pos.z, a.def.radius)) {
        const idx = nearestWalkable(grid, s.pos);
        if (idx >= 0) {
          const c = grid.cellToWorld(idx % grid.w, (idx / grid.w) | 0);
          s.pos.x = c.x;
          s.pos.z = c.z;
        }
      }
    }

    if (a.turnRate > 0) s.yaw = turnToward(s.yaw, a.desiredYaw, a.turnRate * dt);
  }

  /**
   * Keep bodies from occupying the same square metre. Cheap; n is tiny. The
   * push goes through `slideMove` so separating two entities in a doorway can
   * never shove one of them into the wall, or into the furniture.
   */
  private separate(grid: Grid): void {
    const n = this.agents.length;
    for (let i = 0; i < n; i++) {
      const a = this.agents[i]!;
      if (a.asleep) continue;
      // A frozen body is immovable for the same reason a clump is: being shoved
      // half a metre by a pack-mate while the player is looking straight at it
      // would break the one promise the freeze makes. The other body still gets
      // pushed clear, so nothing ends up standing inside it.
      const aMoves = a.def.walkSpeed > 0 && !a.frozen;
      for (let j = i + 1; j < n; j++) {
        const b = this.agents[j]!;
        if (b.asleep) continue;
        const bMoves = b.def.walkSpeed > 0 && !b.frozen;
        if (!aMoves && !bMoves) continue;
        const dx = b.state.pos.x - a.state.pos.x;
        const dz = b.state.pos.z - a.state.pos.z;
        const min = a.def.radius + b.def.radius;
        const d2 = dx * dx + dz * dz;
        if (d2 >= min * min || d2 < 1e-6) continue;
        const d = Math.sqrt(d2);
        // When only one of the pair may move it takes the whole correction,
        // or two bodies would stay lodged in each other indefinitely.
        const push = (min - d) * (aMoves && bMoves ? 0.5 : 1);
        const nx = (dx / d) * push;
        const nz = (dz / d) * push;
        if (aMoves) slideMove(grid, a.state.pos, -nx, -nz, a.def.radius, this.props);
        if (bMoves) slideMove(grid, b.state.pos, nx, nz, b.def.radius, this.props);
      }
    }
  }

  private reapRetired(): void {
    for (let i = this.agents.length - 1; i >= 0; i--) {
      const a = this.agents[i]!;
      if (!a.retire) continue;
      this.despawn(a.state.id);
    }
  }

  /* ---------------------------------------------------------------- *
   * Encounter accounting
   * ---------------------------------------------------------------- */

  /** An engaged entity that has calmed down means the encounter is over. */
  private checkResolution(a: Agent): void {
    if (!a.engaged) return;
    const m = a.state.mode;
    if (m === 'wander' || m === 'dormant' || m === 'patrol' || m === 'flee' || m === 'dead') {
      this.resolveIfEngaged(a);
    }
  }

  private resolveIfEngaged(a: Agent): void {
    if (!a.engaged) return;
    a.engaged = false;
    // The hunt is over the moment the encounter it was for is over. Without
    // this a hound that crossed the floor, lost the player and calmed down
    // would keep its raised sleep radius for the rest of the countdown and
    // trail them around the level with nothing driving it.
    a.longHunt = 0;
    a.surgeHunt = false;
    this.director.encounterResolved();
  }

  /** Too far to matter. Take it away so the director can decide afresh later. */
  private recycle(a: Agent): void {
    this.despawn(a.state.id);
  }

  /* ---------------------------------------------------------------- *
   * EntitySystem surface
   * ---------------------------------------------------------------- */

  spawn(kind: EntityKind, pos: Vec2): EntityState | null {
    return this.spawnAgent(kind, pos)?.state ?? null;
  }

  /** As `spawn`, but hands back the agent so the caller can mark it. */
  private spawnAgent(kind: EntityKind, pos: Vec2): Agent | null {
    if (!this.grid) return null;
    const def = getEntityDef(kind);
    const id = this.nextId++;
    const firstOfRun = !this.met.has(kind);
    this.met.add(kind);

    const state: EntityState = {
      id,
      kind,
      pos: { x: pos.x, z: pos.z },
      yaw: 0,
      vel: { x: 0, z: 0 },
      mode: 'dormant',
      health: def.health,
      awareness: 0,
      lastKnown: null,
      modeTime: 0,
      age: 0,
      distToPlayer: Infinity,
      visible: false,
    };

    const agent: Agent = {
      state,
      def,
      behavior: getBehavior(kind),
      path: makePath(),
      rng: this.rng.fork(id),
      thinkAccum: THINK_INTERVAL,
      repathCooldown: 0,
      attackCooldown: 0,
      heardPos: { x: 0, z: 0 },
      heardPending: false,
      heardLoudness: 0,
      searchTimer: 0,
      timer: 0,
      timer2: 0,
      counter: 0,
      lookLatch: false,
      sighted: false,
      engaged: false,
      firstOfRun,
      dest: { x: pos.x, z: pos.z },
      goal: { x: pos.x, z: pos.z },
      goalSpeed: 0,
      desiredYaw: 0,
      turnRate: 3,
      anchor: { x: pos.x, z: pos.z },
      resting: false,
      stuck: 0,
      snags: 0,
      propGrace: 0,
      asleep: false,
      retire: false,
      longHunt: 0,
      surgeHunt: false,
      freezeRange: 0,
      frozen: false,
    };
    state.yaw = agent.desiredYaw;

    // The director picks spawn cells off the grid, which knows nothing about
    // the furniture standing on it. A zero-length move is the ejection on its
    // own, walls and all, and puts the body beside whatever it landed in
    // before anybody is told where it is.
    if (this.props && this.grid) slideMove(this.grid, state.pos, 0, 0, def.radius, this.props);

    this.agents.push(agent);
    this.list.push(state);

    // Behaviours may nudge themselves somewhere better (a smiler finds the dark)
    // before anyone is told where they are.
    agent.behavior.spawn?.(agent, this.world);

    this.events.emit('entity:spawned', { id, kind, pos: { x: state.pos.x, z: state.pos.z } });
    return agent;
  }

  despawn(id: number): void {
    const i = this.agents.findIndex((a) => a.state.id === id);
    if (i < 0) return;
    const a = this.agents[i]!;
    // Removing something mid-encounter still ends the encounter.
    this.resolveIfEngaged(a);
    this.agents.splice(i, 1);
    const j = this.list.indexOf(a.state);
    if (j >= 0) this.list.splice(j, 1);
    this.nav.cancel(id);
    this.events.emit('entity:despawned', { id, kind: a.state.kind });
  }

  /**
   * Emptied in place: `game.ts` captures this array once when it builds the
   * frame context. No `entity:despawned` goes out — this is a teardown between
   * levels, not six things leaving the room.
   */
  clear(): void {
    for (const a of this.agents) this.nav.cancel(a.state.id);
    this.agents.length = 0;
    this.list.length = 0;
    this.threat = 0;
    this.cursor = 0;
  }

  /**
   * A one-off noise happened. Anything within earshot gets handed a position —
   * this is the hound's only real sense.
   */
  hearNoise(pos: Vec2, loudness: number): void {
    const grid = this.grid;
    if (!grid || loudness <= 0) return;
    for (const a of this.agents) {
      if (a.asleep) continue;
      const heard = noiseAt(a.def, grid, a.state.pos, pos, loudness);
      if (heard < TUNING.entities.noiseThreshold) continue;
      if (a.heardPending && a.heardLoudness > heard) continue;
      a.heardPos.x = pos.x;
      a.heardPos.z = pos.z;
      a.heardLoudness = heard;
      a.heardPending = true;
    }
  }

  dispose(): void {
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
    this.clear();
  }
}

/**
 * Metres this agent may be from the player before it is put to sleep.
 *
 * The ordinary answer is `activeRadius` and it is the answer for anything that
 * is not currently hunting. An agent that is — because it has picked the player
 * up, or because the slide surge placed it across the floor on purpose — holds
 * a licence for a raised one, since the ordinary rule would otherwise put it to
 * sleep and wipe what it knows in the middle of the approach. See
 * `Agent.longHunt`.
 */
function activeRadiusFor(a: Agent): number {
  return a.longHunt > 0 ? TUNING.entities.pursuit.activeRadius : TUNING.entities.activeRadius;
}

/** Metres this agent may be from the player before it is recycled. As above. */
function despawnRadiusFor(a: Agent): number {
  return a.longHunt > 0 ? TUNING.entities.pursuit.despawnRadius : TUNING.entities.despawnRadius;
}

/** Honest danger from one entity: how sure it is, how close, and what it is doing. */
function threatOf(a: Agent): number {
  const s = a.state;
  const mode = MODE_THREAT[s.mode];
  if (mode <= 0) return 0;
  // Full weight inside 3m, nothing past 27m.
  const proximity = smoothstep(27, 3, s.distToPlayer);
  if (proximity <= 0) return 0;
  const known = s.mode === 'chase' || s.mode === 'attack' ? 1 : Math.max(s.awareness, s.visible ? 0.5 : 0);
  return clamp01(known * mode * proximity * KIND_THREAT[s.kind]);
}

export function makeEntitySystem(events: EventBus): EntitySystem {
  return new Entities(events);
}
